/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Group, Image, Paper } from '@mantine/core';

// Styles
import classes from './productions.module.css';

// Components
import { Button } from '@components/atoms/Button';
import { LightButton } from '@components/atoms/LightButton';
import { Pagination } from '@components/atoms/Pagination';
import { Text } from '@components/atoms/Text';
import { Tooltip } from '@components/atoms/Tooltip';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { DateFilter } from '@components/molecules/DateFilter';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { MultipleSelectionModal } from '@components/molecules/MultipleSelectionModal';
import { Datatable } from '@components/organisms/Datatable';
import { AddProduction } from '@features/sourcing/productions/components/AddProduction';
import { DrawerProduction } from '@features/sourcing/productions/components/DrawerProduction';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';
import {
	IconCheck,
	IconCirclePlus,
	IconDownload,
	IconPlayerPlay,
	IconTrash,
} from '@tabler/icons-react';

// Apis
import { useDownload } from '@features/sourcing/productions/api/download';
import { useProductions } from '@features/sourcing/productions/api/get-productions';
import { useMint } from '@features/sourcing/productions/api/mint';
import { useRemove } from '@features/sourcing/productions/api/remove';
import { useRemoveMultiple } from '@features/sourcing/productions/api/removeMultiple';
import { useVerify } from '@features/sourcing/productions/api/verify';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useSites } from 'shared/api/get-sites';

// Datatable config
import { tableSchemaBase as columns } from '@features/sourcing/productions/productions.columns';
import { tableSchemaBase as columnsWithoutContact } from '@features/sourcing/productions/productionsWithContact.columns';

// Utils
import {
	convertArrayToLabelValue,
	getVisibleColumns,
	truncateString,
} from '@utils/functions';

// Options
import {
	MINT_STATUS_FILTER_OPTIONS,
	QUANTITY_CHECK_FILTER_OPTIONS,
	VERIFIED_FILTER_OPTIONS,
} from '@utils/options';

// Custom hook
import { ResetButton } from '@components/atoms/ResetButton';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { useProduction } from '@features/sourcing/productions/hooks/useProduction';
import { TTargetColumn } from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import { useMediaQuery } from '@mantine/hooks';
import useStore from '@store/index';
import { addFooterColumn } from '@utils/datatable';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Productions = () => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	const { t } = useTranslation();
	const {
		getUser,
		setProductionsDatatableOptions,
		productionsDatatableOptions,
		productionsWithoutContactDatatableOptions,
		setProductionsWithoutContactDatatableOptions,
	} = useStore();

	const user = getUser();

	const {
		containerRef,
		productionsPage,
		productionsPageSize,
		productionsSelectedRecords,
		productionsOpenConfirmationModalDelete,
		productionsOpenConfirmationModalVerify,
		productionsOpenConfirmationModalMint,
		productionsQueryParams,
		productionsSortStatus,
		productionsSortParams,
		productionsSelectedRow,
		queryParamsProjects,
		handleScroll,
		resetFilterSites,
		setResetFilterSites,
		isOverflowFilters,
		openDrawer,
		openSelectedItemsToDownloadModal,
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		openDrawerAddProduction,
		setOpenDrawerAddProduction,
		handleapplyprojectfilter,
		handleStartMint,
		hasStartDate,
		NBR_SELECTED_ROWS,
		ref,
		handleCloseDrawerAddProduction,
		handleverifyproduction,
		handledeleteproduction,
		handleCloseDrawerShowMoreProduction,
		handlestartmint,
		handledownloadmultipleproductions,
		handleclosedownloadmultipleselection,
		handleremovedownloadmultipleselection,
		handleverifymultipleselectionconfirm,
		handlecloseverifymultipleselectionmodal,
		handleremoveverifymultipleselection,
		handledeletemultipleselectionconfirm,
		handleclosedeletemultipleselectionmodal,
		handleremovedeletemultipleselection,
		handledeleteconfirmationmodalconfirm,
		handleclosedeleteconfirmationmodal,
		handleverifyconfirmationmodalconfirm,
		handlecloseverifyconfirmationmodal,
		handlemintconfirmationmodalconfirm,
		handleclosemintconfirmationmodal,
		handlerowclicked,
		handlerowviewmore,
		handleVerifyProduction,
		handlerowmint,
		handlerowremoveitem,
		handlechangesortstatus,
		handleselectedrecordchange,
		handlepaginationchangesize,
		handlechangepagination,
		handleapplydatefilter,
		handleapplystatusfilter,
		handleapplysitefilter,
		handleapplymintstatusfilter,
		handledownloadaction,
		handleResetSelectedItems,
		handleapplyquantitycheckfilter,
		debouncedPageSize,
	} = useProduction();

	// APIs
	const { data: projects } = useMeasurabilities();

	const { data: sites } = useSites(queryParamsProjects);

	const verify = useVerify(
		productionsPage,
		productionsPageSize
	);

	const mint = useMint(
		productionsPage,
		productionsPageSize
	);

	const remove = useRemove(
		productionsPage,
		productionsPageSize
	);

	const download = useDownload(
		productionsPage,
		productionsPageSize,
		productionsQueryParams
	);

	const removeMultiple = useRemoveMultiple(
		productionsPage,
		productionsPageSize
	);

	const NOT_VERIFIED_ITEM_EXIST =
		productionsSelectedRecords.findIndex(
			(item: { verified: boolean }) => !item.verified
		) !== -1;

	const { checkAcl, checkRole } = useAuthorization();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useProductions(
		productionsPage,
		debouncedPageSize,
		productionsQueryParams,
		productionsSortParams
	);

	const IS_ENABLE_TO_DOWNLOAD =
		(productionsSelectedRecords &&
			productionsSelectedRecords.length > 0) ||
		(hasStartDate(productionsQueryParams) &&
			data &&
			data.length > 0);

	return (
		<Box className={classes.container} ref={ref}>
			{/**************************** Drawer add production ****************************/}
			<AddProduction
				opened={openDrawerAddProduction}
				onClose={handleCloseDrawerAddProduction}
			/>

			{/**************************** Drawer show more production ****************************/}
			<DrawerProduction
				opened={openDrawer}
				handleverify={(item) =>
					handleverifyproduction(item, verify)
				}
				handledelete={handledeleteproduction}
				handlestartmint={handlestartmint}
				onClose={handleCloseDrawerShowMoreProduction}
				position={'right'}
				data={productionsSelectedRow}
			/>

			{/**************************** Download Multiple selection modal ****************************/}

			{Array.isArray(productionsSelectedRecords) &&
				productionsSelectedRecords.length > 0 && (
					<MultipleSelectionModal
						model="production"
						type="download"
						data={productionsSelectedRecords}
						onConfirm={() =>
							handledownloadmultipleproductions(
								user,
								download
							)
						}
						onClose={handleclosedownloadmultipleselection}
						onRemove={
							handleremovedownloadmultipleselection
						}
						opened={openSelectedItemsToDownloadModal}
					/>
				)}
			{/**************************** Verify Multiple selection modal ****************************/}

			{Array.isArray(productionsSelectedRecords) &&
				productionsSelectedRecords.length > 0 && (
					<MultipleSelectionModal
						model="production"
						type="verify"
						data={productionsSelectedRecords}
						onConfirm={() =>
							handleverifymultipleselectionconfirm(verify)
						}
						onClose={
							handlecloseverifymultipleselectionmodal
						}
						onRemove={handleremoveverifymultipleselection}
						opened={openSelectedItemsToVerifyModal}
					/>
				)}
			{/**************************** Delete Multiple selection modal ****************************/}

			{Array.isArray(productionsSelectedRecords) &&
				productionsSelectedRecords.length > 0 && (
					<MultipleSelectionModal
						model="production"
						type="delete"
						data={productionsSelectedRecords}
						onConfirm={() =>
							handledeletemultipleselectionconfirm(
								removeMultiple
							)
						}
						onClose={
							handleclosedeletemultipleselectionmodal
						}
						onRemove={handleremovedeletemultipleselection}
						opened={openSelectedItemsToDeleteModal}
					/>
				)}
			{/**************************** Delete confirmation modal ****************************/}
			<ConfirmationModal
				title={t('productions.modal.delete.title')}
				description={t(
					'productions.modal.delete.description'
				)}
				onConfirm={() =>
					handledeleteconfirmationmodalconfirm(remove)
				}
				onClose={handleclosedeleteconfirmationmodal}
				opened={
					productionsOpenConfirmationModalDelete &&
					productionsOpenConfirmationModalDelete.open
				}
			/>

			{/**************************** Verify confirmation modal ****************************/}

			<ConfirmationModal
				title={t('productions.modal.verify.title')}
				description={t(
					'productions.modal.verify.description'
				)}
				onConfirm={() =>
					handleverifyconfirmationmodalconfirm(verify)
				}
				onClose={handlecloseverifyconfirmationmodal}
				opened={
					productionsOpenConfirmationModalVerify &&
					productionsOpenConfirmationModalVerify.open
				}
			/>
			{/**************************** Mint confirmation modal ****************************/}

			<ConfirmationModal
				title={t('productions.modal.mint.title')}
				description={t(
					'productions.modal.mint.description'
				)}
				onConfirm={() =>
					handlemintconfirmationmodalconfirm(mint)
				}
				onClose={handleclosemintconfirmationmodal}
				opened={
					productionsOpenConfirmationModalMint &&
					productionsOpenConfirmationModalMint.open
				}
			/>
			{/**************************** Content ****************************/}

			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<DateFilter
											onApply={handleapplydatefilter}
											placeholder={t(
												'placeholders.searchProject'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={handleapplystatusfilter}
											label={t('global.verifiedStatus')}
											size="lg"
											options={VERIFIED_FILTER_OPTIONS}
											placeholderSearch={t(
												'filters.placeholderSearch.searchVerifiedStatus'
											)}
											placeholder={t(
												'filters.placeholders.selectVerifiedStatus'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									{!checkRole([
										ROLES.PRODUCER_ADMIN,
										ROLES.PRODUCER_STAFF,
									]) && (
										<>
											<Box
												className={
													classes.containerFilterSelect
												}
											>
												<MenuFilter
													onApply={
														handleapplyprojectfilter
													}
													label={t('global.projects')}
													size="lg"
													options={convertArrayToLabelValue(
														projects
													)}
													placeholderSearch={t(
														'filters.placeholderSearch.searchProject'
													)}
													placeholder={t(
														'filters.placeholders.selectProject'
													)}
												/>
											</Box>
											<Box
												className={classes.spacerFilter}
											/>
										</>
									)}
									{checkRole([
										ROLES.PRODUCER_ADMIN,
										ROLES.PRODUCER_STAFF,
									]) &&
										projects &&
										Array.isArray(projects) &&
										projects.length > 1 && (
											<>
												<Box
													className={
														classes.containerFilterSelect
													}
												>
													<MenuFilter
														onApply={
															handleapplyprojectfilter
														}
														label={t('global.projects')}
														size="lg"
														options={convertArrayToLabelValue(
															projects
														)}
														placeholderSearch={t(
															'filters.placeholderSearch.searchProject'
														)}
														placeholder={t(
															'filters.placeholders.selectProject'
														)}
													/>
												</Box>
												<Box
													className={classes.spacerFilter}
												/>
											</>
										)}
									{!checkRole([
										ROLES.PRODUCER_ADMIN,
										ROLES.PRODUCER_STAFF,
										ROLES.ORGANIZATION_ADMIN,
									]) && (
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												reset={resetFilterSites}
												handlereset={(
													_isReset: boolean
												) => {
													setResetFilterSites(_isReset);
												}}
												onApply={handleapplysitefilter}
												label={t('global.sites')}
												size="lg"
												options={convertArrayToLabelValue(
													sites
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.searchSite'
												)}
												placeholder={t(
													'filters.placeholders.selectSite'
												)}
											/>
										</Box>
									)}
									<Box className={classes.spacerFilter} />
									{projects &&
										Array.isArray(projects) &&
										projects.length > 1 && (
											<>
												<Box
													className={
														classes.containerFilterSelect
													}
												>
													<MenuFilter
														onApply={
															handleapplymintstatusfilter
														}
														label={t('global.status')}
														size="lg"
														options={
															MINT_STATUS_FILTER_OPTIONS
														}
														placeholderSearch={t(
															'filters.placeholderSearch.searchStatus'
														)}
														placeholder={t(
															'filters.placeholders.selectStatus'
														)}
													/>
												</Box>
												<Box
													className={classes.spacerFilter}
												/>
											</>
										)}
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={
												handleapplyquantitycheckfilter
											}
											label={t('global.quantityCheck')}
											size="lg"
											options={
												QUANTITY_CHECK_FILTER_OPTIONS
											}
											placeholderSearch={t(
												'filters.placeholderSearch.searchQuantityCheck'
											)}
											placeholder={t(
												'filters.placeholders.selectQuantityCheck'
											)}
										/>
									</Box>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkRole([
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					]) &&
						checkAcl(ACLS.PRODUCTIONS.ADMIN) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconPlayerPlay size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									variant="outline"
									onClick={handleStartMint}
								>
									{t('global.startMint')}
								</Button>
								<Button
									leftSection={
										<IconPlayerPlay size={23} />
									}
									size="lg"
									variant="outline"
									visibleFrom="xxl"
									onClick={handleStartMint}
								>
									{t('global.startMint')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={() => {
										setOpenDrawerAddProduction(true);
									}}
								>
									{t('productions.addProduction')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={() => {
										setOpenDrawerAddProduction(true);
									}}
								>
									{t('productions.addProduction')}
								</Button>
							</div>
						)}
				</Box>
				<Box className={classes.spacerRow} />
				{/* Row actions */}

				{checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				]) &&
					checkAcl(ACLS.PRODUCTIONS.ADMIN) && (
						<Box className={classes.containerRow}>
							<Group
								gap={'10px'}
								className={classes.containerRowLeft}
							>
								<Tooltip
									label={NBR_SELECTED_ROWS.toString()}
									isActive={
										NBR_SELECTED_ROWS.toString().length >
										4
									}
									position={'left'}
								>
									<Box
										className={
											classes.containerNbrSelection
										}
									>
										<Text
											type="body2"
											color="inputs-texts-icons"
											size="xs"
										>
											{'('}
											{productionsSelectedRecords
												? truncateString(
														NBR_SELECTED_ROWS.toString(),
														4
													)
												: 0}
											{') '}
											{t('global.selected')}
										</Text>
										{/* Reset selection action */}
										<ResetButton
											disabled={
												productionsSelectedRecords &&
												productionsSelectedRecords.length ==
													0
											}
											variant="transparent"
											size="xs"
											onClick={handleResetSelectedItems}
										/>
									</Box>
								</Tooltip>

								{/* Download action */}

								<LightButton
									disabled={!IS_ENABLE_TO_DOWNLOAD}
									leftSection={<IconDownload size={18} />}
									size={isBigScreen ? 'lg' : 'md'}
									onClick={() =>
										handledownloadaction(user, download)
									}
								>
									{t('global.download')}
								</LightButton>

								{/* Verify action */}
								<LightButton
									disabled={!NOT_VERIFIED_ITEM_EXIST}
									leftSection={<IconCheck size={18} />}
									size={isBigScreen ? 'lg' : 'md'}
									onClick={() => {
										setOpenSelectedItemsToVerifyModal(
											true
										);
									}}
								>
									{t('global.verify')}
								</LightButton>
								{/* Delete action */}
								<LightButton
									disabled={!NOT_VERIFIED_ITEM_EXIST}
									leftSection={<IconTrash size={18} />}
									size={isBigScreen ? 'lg' : 'md'}
									onClick={() => {
										setOpenSelectedItemsToDeleteModal(
											true
										);
									}}
								>
									{t('global.delete')}
								</LightButton>
							</Group>
						</Box>
					)}

				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? setProductionsDatatableOptions(
										getVisibleColumns(columns, cols)
									)
								: setProductionsWithoutContactDatatableOptions(
										getVisibleColumns(
											columnsWithoutContact,
											cols
										)
									);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={addFooterColumn(
							data,
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? columns
								: columnsWithoutContact
						)}
						columns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? productionsDatatableOptions
								: productionsWithoutContactDatatableOptions
						}
						data={data}
						onRowClick={handlerowclicked}
						onViewMore={handlerowviewmore}
						onVerify={(row: any) => {
							handleVerifyProduction(row, verify);
						}}
						onMint={handlerowmint}
						onRemoveItem={handlerowremoveitem}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && checkAcl(ACLS.PRODUCTIONS.ADMIN)
								? productionsSelectedRecords
								: null
						}
						onSelectedRecordsChange={
							handleselectedrecordchange
						}
						onSortStatusChange={handlechangesortstatus}
						sortstatus={productionsSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					maxpagesize={1000}
					pagesize={productionsPageSize}
					totalitems={totalItems}
					activepage={productionsPage}
					handlechangesize={handlepaginationchangesize}
					onChange={handlechangepagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default Productions;
